"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader, CalendarDays, MapPin, Package, Clock, User, Mail, Phone } from "lucide-react";
export default function LetSTalkNumbers() {
  return <section data-section-id="223898" id="lets-talk-numbers" className="bg-background text-foreground py-24 px-4 md:px-8 relative overflow-hidden">
      {}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-lg bg-primary/10 blur-[120px]" />
        <div className="absolute -bottom-[20%] -left-[10%] w-[50%] h-[50%] rounded-lg bg-secondary/10 blur-[120px]" />
      </div>

      <div className="max-w-3xl mx-auto relative z-10">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-default font-bold mb-4 tracking-tighter text-foreground">
            Let's Talk Numbers
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto font-default">
            Ready to optimize your financial health? Share your business details below and our accounting experts will craft a precision strategy for your growth.
          </p>
        </div>

        <FormProvider formKey="talk_numbers_form_get_started" formId="talk_numbers_form_get_started" sectionName="ScheduleACollection" defaultValues={{
        name: "",
        email: "",
        phone: "",
        address: "",
        collection_date: undefined,
        time_slot: "",
        bags: ""
      }} validationRules={{
        name: {
          required: true
        },
        email: {
          required: true,
          pattern: "email"
        },
        phone: {
          required: true
        },
        address: {
          required: true
        },
        collection_date: {
          required: true
        },
        time_slot: {
          required: true
        },
        bags: {
          min: 1,
          max: 500
        }
      }} submitText="Request Consultation" successMessage="Strategy session requested. A LEDGER specialist will reach out within 24 hours with your preliminary financial assessment.">
          <CollectionForm />
        </FormProvider>
      </div>
    </section>;
}
function CollectionForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <Card className="border-primary/30 bg-card/40 backdrop-blur-md shadow-2xl rounded-lg">
        <CardContent className="pt-12 pb-12 flex flex-col items-center text-center space-y-6">
          <div className="size-16 rounded-lg bg-primary/20 flex items-center justify-center text-primary mb-4">
            <Package className="size-8" />
          </div>
          <h3 className="text-2xl font-default font-bold tracking-tight">Inquiry Received</h3>
          <p className="text-muted-foreground max-w-md font-default">
            {successMessage}
          </p>
          <Button onClick={resetForm} variant="outline" className="mt-4 border-primary text-primary hover:bg-primary/10 rounded-md">
            Submit Another Inquiry
          </Button>
        </CardContent>
      </Card>;
  }
  return <Card className="shadow-4xl border-border/60 bg-card/90 backdrop-blur-xl rounded-lg">
      <CardHeader className="pb-8 border-b border-border/30">
        <CardTitle className="text-xl font-default font-semibold flex items-center gap-2">
          <MapPin className="size-5 text-secondary" />
          Business Profile
        </CardTitle>
        <CardDescription className="font-default">
          Provide your operational details for a tailored bookkeeping assessment.
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-8">
        <Form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {}
            <FormField control={control} name="name" rules={{
            required: "Full name is required"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="flex items-center gap-2 font-default font-medium">
                    <User className="size-4 text-muted-foreground" />
                    Contact Name
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="Jane Smith" className="bg-input/50 border-border focus:ring-secondary/50 rounded-md font-default" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            {}
            <FormField control={control} name="email" rules={{
            required: "Business email is required",
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: "Invalid email address"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="flex items-center gap-2 font-default font-medium">
                    <Mail className="size-4 text-muted-foreground" />
                    Business Email
                  </FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="jane@company.com" className="bg-input/50 border-border focus:ring-secondary/50 rounded-md font-default" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            {}
            <FormField control={control} name="phone" rules={{
            required: "Phone number is required"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="flex items-center gap-2 font-default font-medium">
                    <Phone className="size-4 text-muted-foreground" />
                    Direct Phone
                  </FormLabel>
                  <FormControl>
                    <Input type="tel" placeholder="+1 (555) 000-0000" className="bg-input/50 border-border focus:ring-secondary/50 rounded-md font-default" {...field} />
                  </FormControl>
                  <FormDescription className="text-xs font-mono">Secure line for financial verification</FormDescription>
                  <FormMessage />
                </FormItem>} />

            {}
            <FormField control={control} name="bags" rules={{
            min: {
              value: 1,
              message: "Minimum 1 employee"
            },
            max: {
              value: 500,
              message: "Maximum 500 employees"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="flex items-center gap-2 font-default font-medium">
                    <Package className="size-4 text-muted-foreground" />
                    Total Employees
                  </FormLabel>
                  <FormControl>
                    <Input type="number" min="1" max="500" placeholder="e.g., 15" className="bg-input/50 border-border focus:ring-secondary/50 rounded-md font-mono" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            {}
            <div className="md:col-span-2">
              <FormField control={control} name="address" rules={{
              required: "Business address is required"
            }} render={({
              field
            }) => <FormItem>
                    <FormLabel className="flex items-center gap-2 font-default font-medium">
                      <MapPin className="size-4 text-muted-foreground" />
                      Registered Business Address
                    </FormLabel>
                    <FormControl>
                      <textarea className="flex min-h-[100px] w-full rounded-md border border-input bg-input/50 px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-secondary/50 disabled:cursor-not-allowed disabled:opacity-50 font-default resize-y" placeholder="Street address, Suite, City, State, ZIP" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
            </div>

            {}
            <div className="md:col-span-1 flex flex-col">
              <FormField control={control} name="collection_date" rules={{
              required: "Please select a consultation date"
            }} render={({
              field
            }) => <FormItem className="flex flex-col h-full">
                    <FormLabel className="flex items-center gap-2 font-default font-medium">
                      <CalendarDays className="size-4 text-muted-foreground" />
                      Preferred Strategy Date
                    </FormLabel>
                    <div className="rounded-md border border-input bg-background/50 p-3 flex-1 flex items-center justify-center shadow-inner">
                      <SafeHydrate><Calendar mode="single" selected={field.value} onSelect={field.onChange} disabled={date => {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    return date < today || date.getDay() === 0 || date.getDay() === 6;
                  }} className="pointer-events-auto font-mono" /></SafeHydrate>
                    </div>
                    <FormDescription className="text-xs font-default">Available Monday through Friday</FormDescription>
                    <FormMessage />
                  </FormItem>} />
            </div>

            {}
            <div className="md:col-span-1 flex flex-col justify-start">
              <FormField control={control} name="time_slot" rules={{
              required: "Please select a time slot"
            }} render={({
              field
            }) => <FormItem>
                    <FormLabel className="flex items-center gap-2 font-default font-medium">
                      <Clock className="size-4 text-muted-foreground" />
                      Consultation Window
                    </FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger className="w-full bg-input/50 border-border rounded-md font-default">
                          <SelectValue placeholder="Select a time slot" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent className="bg-popover border-border">
                        {["Early Morning (8am-10am)", "Mid-Day (11am-2pm)", "Late Afternoon (3pm-5pm)"].map((slot, i) => <SelectItem key={i} value={slot} data-index={i} className="font-default">
                            {slot}
                          </SelectItem>)}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>} />
            </div>
          </div>

          {submitError && <div className="mt-6 p-4 bg-destructive/10 border border-destructive/20 rounded-md">
              <p className="text-destructive text-sm font-default font-medium">{submitError}</p>
            </div>}

          <div className="mt-8 pt-6 border-t border-border/30 flex justify-end">
            <Button type="submit" size="lg" disabled={isSubmitting} className="w-full md:w-auto min-w-[220px] bg-gradient-to-r from-secondary to-primary text-primary-foreground font-default font-bold rounded-md shadow-lg hover:shadow-secondary/20 transition-all duration-300">
              {isSubmitting ? <>
                  <Loader className="mr-2 size-4 animate-spin" />
                  Processing...
                </> : "Request Consultation"}
            </Button>
          </div>
        </Form>
      </CardContent>
    </Card>;
}